import React from 'react'
import { useAppDispatch, useAppSelector } from '../redux'

import { selectTeams, fetchTeams } from '../../slices/teamsSlice'
import useAccess, { PERMS } from '../useAccess'
import { useEnvironment } from '../../providers/EnvironmentProvider'

const useTaskPermissions = (task: LocalTask) => {
  const dispatch = useAppDispatch()

  const {
    teams,
    loaded: teamsLoaded,
    loading: teamsLoading
  } = useAppSelector(selectTeams)

  const hasPermission = useAccess()
  const { user } = useEnvironment()

  // Load teams if needed
  React.useEffect(() => {
    if (!teamsLoaded && !teamsLoading) {
      dispatch(fetchTeams())
    }
  }, [teamsLoaded, teamsLoading, dispatch])

  // Checks if you are an assignee
  const canModifyOwn = React.useMemo(() => {
    if (!task) return false
    const {
      primary_assignee_id,
      assignee_ids = [],
      owner_id,
      watcher_ids = []
    } = task
    return [...assignee_ids, primary_assignee_id, owner_id, ...watcher_ids]
      .filter(Boolean)
      .includes(user?.id)
  }, [task, user])

  // Only leaders can modify team tasks
  const canModifyTeams = React.useMemo(() => {
    if (!task) return false

    const { team_id } = task
    const isTeamLeader = teams
      .find(team => team.id === team_id)
      ?.leaders.includes(user?.id)

    if (isTeamLeader) {
      return true
    }

    return false
  }, [task, teams, user])

  // Updates are allowed on your tasks if you are an assignee
  const canUpdate = React.useMemo(() => {
    if (hasPermission(PERMS.TasksOwn)) {
      return canModifyOwn
    }

    return false
  }, [hasPermission, canModifyOwn])

  // Editing is only allowed with full, or team / own permissions
  const canEdit = React.useMemo(() => {
    if (hasPermission(PERMS.TasksFull)) {
      return true
    }

    //check if its leader of the team, if not continue to check if is owner
    if (hasPermission(PERMS.TasksTeam)) {
      if (canModifyTeams) {
        return true
      }
    }

    if (hasPermission(PERMS.TasksOwn)) {
      return canModifyOwn
    }

    return false
  }, [hasPermission, canModifyTeams, canModifyOwn])

  // Deleting is allowed by admins and owners
  const canDelete = React.useMemo(() => {
    if (hasPermission(PERMS.TasksAdmin)) {
      return true
    }

    if (hasPermission(PERMS.TasksOwn)) {
      const { owner_id } = task
      return user?.id === owner_id
    }

    return false
  }, [hasPermission, task, user])

  // Closing is allowed by people with the close permission or people who can delete
  const canClose = React.useMemo(() => {
    if (hasPermission(PERMS.TasksClose)) {
      return true
    }

    return canDelete
  }, [hasPermission, canDelete])

  const canView = React.useMemo(() => {
    if (!task) return false

    if (hasPermission(PERMS.TasksAudit)) {
      return true
    }

    const hasTeam = hasPermission(PERMS.TasksTeam)
    const hasOwn = hasPermission(PERMS.TasksOwn)

    if (hasTeam || hasOwn) {
      let result = false

      if (hasTeam) {
        result = user?.teams?.findIndex(team => team.id === task.team_id) !== -1
      }
      if (hasOwn) {
        result = result || canModifyOwn
      }

      return result
    }

    return false
  }, [hasPermission, user, task, canModifyOwn])

  const disabled = React.useMemo(
    () => !canEdit && !canUpdate,
    [canEdit, canUpdate]
  )

  return { canUpdate, canEdit, canDelete, canClose, canView, disabled }
}

export default useTaskPermissions
